import React from 'react';
import { useDraggable } from '@dnd-kit/core';

import { blockPosition } from './PageLayoutRender';
import PageGroupHandles from './PageGroupHandles';
import { CLOSE } from '../icons';

/**
 * The page layout editor's handles: a palette pill and a placed block's drag
 * surface and resize corner. Moved out of PageLayoutEditorPage (KPR-1287) to
 * keep that file under the size ceiling; the behavior is unchanged.
 */

export const PALETTE = 'palette:';
export const NUDGE_HELP_ID = 'page-editor-nudge-help';

export function PalettePill({ id, label, hint, onInsert }) {
	const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: PALETTE + id });
	return (
		<button
			ref={setNodeRef}
			type="button"
			className="page-editor__pill"
			style={{ opacity: isDragging ? 0.6 : 1 }}
			onClick={() => onInsert(id)}
			{...attributes}
			{...listeners}
		>
			<span className="page-editor__pill-name">{label}</span>
			{hint ? <span className="page-editor__pill-hint">{hint}</span> : null}
		</button>
	);
}

// The arrow keys, as the way they point: a nudge moves a block this way (KPR-348).
const NUDGES = { ArrowLeft: { dx: -1 }, ArrowRight: { dx: 1 }, ArrowUp: { dy: -1 }, ArrowDown: { dy: 1 } };

/**
 * A placed block's drag surface and its resize corner.
 *
 * They live in a layer that has the block's own box, so each handle belongs to
 * ITS block: with `inset: 0` taken against the sheet, every block's grab
 * surface covered the whole page and a pointer drag anywhere moved whichever
 * block was drawn last. A rule has no height, so its layer is a 14px band
 * centred on the line, enough to grab.
 */
export function BlockHandles({ block, page, sheet = page, selected, carrying, cut = null, group = null, onSelect, onRemove, onNudge }) {
	const move = useDraggable({ id: `move:${block.id}` });
	const size = useDraggable({ id: `size:${block.id}` });
	// Spread FIRST and compose after: dnd-kit's listeners carry their own
	// onKeyDown (Space / Enter picks the block up), and a second onKeyDown
	// written before the spread was silently replaced by it.
	const onGrabKeyDown = (event) => {
		// While a block is carried the arrows are dnd-kit's (its own listener on the
		// document moves the block); a nudge as well would move it twice.
		if (carrying) return;
		const nudge = NUDGES[event.key];
		if (nudge && !event.ctrlKey && !event.metaKey && !event.altKey) {
			// Not the canvas scrolling under the person's hand.
			event.preventDefault();
			onNudge(block.id, nudge, event.shiftKey);
			return;
		}
		if (event.key === 'Enter' || event.key === ' ') onSelect(block.id);
		move.listeners?.onKeyDown?.(event);
	};
	const box = blockPosition(block, page);
	if (block.type === 'rule') {
		box.top = `calc(${box.top} - 7px)`;
		box.height = '14px';
	}
	return (
		<div className={`page-block__handles${selected ? ' page-block__handles--selected' : ''}`} style={box}>
			<span
				ref={move.setNodeRef}
				className="page-block__grab"
				role="button"
				tabIndex={0}
				aria-label={`Move ${block.type} block${cut ? `, its sample does not fit: ${cut}` : ''}`}
				onClick={() => onSelect(block.id)}
				{...move.attributes}
				{...move.listeners}
				// dnd-kit's own instruction (pick up, carry, drop) and this editor's nudge.
				aria-describedby={`${move.attributes['aria-describedby'] || ''} ${NUDGE_HELP_ID}`.trim()}
				onKeyDown={onGrabKeyDown}
			/>
			{block.type === 'group' ? <PageGroupHandles block={block} group={group} page={page} sheet={sheet} selected={selected} /> : null}
			{/* In words, under the box: the dashed outline alone would be a colour. The grab handle's name says it too. */}
			{cut ? <span className="page-block__cut" aria-hidden="true">Cut off</span> : null}
			{selected ? (
				<>
					<button
						type="button"
						className="page-block__remove"
						aria-label={`Remove ${block.type} block`}
						onClick={() => onRemove(block.id)}
					>
						<CLOSE aria-hidden="true" />
					</button>
					<span
						ref={size.setNodeRef}
						className="page-block__resize"
						role="button"
						tabIndex={0}
						aria-label={`Resize ${block.type} block`}
						{...size.attributes}
						{...size.listeners}
					/>
				</>
			) : null}
		</div>
	);
}
